import { Field } from "@ark-ui/react/field";
import { Merge } from "type-fest";

import { cva, cx, sva } from "../../styled-system/css";

export const formFieldBoxStyles = cva({
  base: {
    backgroundColor: "white",
    borderColor: "border",
    borderStyle: "solid",
    borderWidth: "1px",
  },

  defaultVariants: {
    borderRadius: "4px",
    height: "40px",
  },
  variants: {
    borderRadius: {
      "4px": { borderRadius: "4px" },
    },
    height: {
      "40px": { height: "40px" },
    },
  },
});

const FormFieldStyle = sva({
  slots: ["errorText", "frame", "helperText", "label", "root"],
  base: {
    errorText: {
      color: "destructive",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.1,
      letterSpacing: 0,
      _empty: { display: "none" },
    },
    helperText: {
      color: "muted-foreground",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.1,
      letterSpacing: 0,
      _empty: { display: "none" },
    },
    label: {
      color: "card-foreground",
      fontSize: "14px",
      fontWeight: 500,
      letterSpacing: 0,
      lineHeight: 1,
      maxWidth: "stretch",
      minWidth: 0,
      overflow: "clip",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      _empty: { display: "none" },
    },
    root: {
      alignItems: "start",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      "&[data-invalid] > [data-scope='field'][data-part='helper-text']": { display: "none" },
      "&:not([data-invalid]) > [data-scope='field'][data-part='error-text']": { display: "none" },
    },
  },
});

export type FormFieldProperties = Merge<
  Field.RootProps,
  {
    /** 注意のテキスト */
    caution?: string;
    /** 説明のテキスト */
    description?: string;
    /** ラベル */
    label?: string;
  }
>;

/**
 * @deprecated 代わりに`FormFieldProperties`を使用する。
 */
export type FormFieldContainerProperties = FormFieldProperties;

/**
 * **フォームフィールドコンテナ**
 * フォームフィールドのラベル、説明、注意を含むコンテナ。
 * `Field.Root`でラップされているため、`Field.Input`をそのまま使用可能。
 * @param properties プロパティ
 * @returns ノード
 */
export function FormField({
  className,
  caution,
  children,
  description,
  label,
  required,
  ...properties
}: FormFieldProperties) {
  const classes = FormFieldStyle();
  return (
    <Field.Root {...properties} className={cx(classes.root, className)}>
      <Field.Label className={classes.label}>
        {label}
        {required && label ? <span>&nbsp;*</span> : null}
      </Field.Label>
      {children}
      <Field.HelperText className={classes.helperText}>{description}</Field.HelperText>
      <Field.ErrorText className={classes.errorText}>{caution}</Field.ErrorText>
    </Field.Root>
  );
}

/**
 * @deprecated 代わりに`FormField`を使用する。
 */
export const FormFieldContainer = FormField;

export const FormFieldInputStyle = cva({
  base: {
    fontSize: "14px",
    lineHeight: 1,
    paddingX: "12px",
    paddingY: "10.5px",
    "&:disabled": { opacity: 0.6 },
  },

  defaultVariants: { border: true },
  variants: {
    backgroundColor: {
      white: { backgroundColor: "white" },
    },
    border: {
      false: {},
      true: {
        borderColor: "border",
        borderRadius: "4px",
        borderStyle: "solid",
        borderWidth: "1px",
      },
    },
    flexGrow: {
      1: { flexGrow: 1 },
    },
    minWidth: {
      0: { minWidth: 0 },
    },
    width: {
      "50%": { width: "50%" },
      "100%": { width: "100%" },
      stretch: { width: "stretch" },
    },
  },
});

/**
 * @deprecated 代わりに`FormFieldInputStyle`を使用する。
 */
export const formFieldInputStyles = FormFieldInputStyle;

export const FormFieldInput = Field.Input;
